import type jsPDF from "jspdf";

import {
  RECEIPT_FALLBACK_FONTS,
  RECEIPT_FONTS,
  RECEIPT_LOGO_ASPECT,
  receiptLogoPng,
  registerReceiptFonts,
} from "@/shared/utils/paymentReceipt/receiptBrand";

/**
 * The brand half of the two collection PDFs, shared with the payout comprobante.
 *
 * These two generators predate the shared receipt and keep their own layout on purpose: a collection's PDF
 * embeds the QR, which is the document an UNPAID collection actually needs, and the payout comprobante has
 * nothing to put a QR on. What they had no business keeping was their own brand — `logo-black.png` (an asset
 * the product stopped using), Helvetica, and a slate palette unrelated to Coloca's. That is all this module
 * replaces; every row, column and QR stays where it was.
 */
export interface PayinPdfFonts {
  sans: string;
  mono: string;
}

/**
 * Registers Outfit and JetBrains Mono on the document and reports what to call them. Falls back to jsPDF's
 * built-ins if the font files cannot be fetched — a document set in Helvetica is still the document, and a
 * failed asset must not cost the reader their download.
 */
export const preparePayinPdfFonts = async (doc: jsPDF): Promise<PayinPdfFonts> => {
  const embedded = await registerReceiptFonts(doc);
  return embedded
    ? { sans: RECEIPT_FONTS.sans, mono: RECEIPT_FONTS.mono }
    : { sans: RECEIPT_FALLBACK_FONTS.sans, mono: RECEIPT_FALLBACK_FONTS.mono };
};

/**
 * Places the current Coloca wordmark, rasterized from the same `logo-pine.svg` the app and the backend's
 * email templates use. Silent on failure, for the same reason the fonts fall back.
 *
 * `width` is in the document's unit; the height follows the mark's real 5.85:1 ratio rather than a hardcoded
 * pair, which is how the old version ended up stretching it by two points.
 */
export const drawPayinPdfLogo = async (doc: jsPDF, x: number, y: number, width: number): Promise<void> => {
  try {
    const logo = await receiptLogoPng();
    doc.addImage(logo, "PNG", x, y, width, width / RECEIPT_LOGO_ASPECT);
  } catch {
    // No wordmark rather than no PDF.
  }
};
